import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Panel from 'components/common/Deal/Panel';
import DealWithRightSidebar from 'components/common/layout/DealWithRightSidebar';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { getMetaData } from 'modules/api/deal';

import { useCheckAvailability } from 'components/common/Deal/hooks/useCheckAvailability';
import { cancelFunctions } from 'utils/cancelFunctions';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import { getDealDetails } from 'store/dealManagement/actions';
import { changeMeta, createNewLineWithUniqueName } from 'store/dealManagement/reducer';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { DealManagementMeta } from 'store/dealManagement/types';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { getIsReseller } from 'store/publisher/selectors';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import ProgrammaticCampaignHeader from 'components/common/Deal/CampaignHeader/ProgrammaticCampaignHeader';
import { useLineImpressionMetrics } from 'components/common/Deal/hooks/useLineImpressionMetrics';
import { Store } from 'components/common/types/Store.types';
import { beginDealViewSwitch } from 'utils/dealSwitchView';
import { hasPlannerOnlyFilters } from 'utils/hasPlannerOnlyFilters';
import RightSidebar from '../common/RightSidebar';
import Main from './Main';

const DealLines = (): React.ReactElement => {
  const { internalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const lineIdFromState = location.state?.lineId;
  const hasAdsDealLevelCPMEnabled = useHasFeatureAccess(FeatureFlags.DEAL_LEVEL_CPM);
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);

  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const isReseller = useSelector(getIsReseller);
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const dealConfig = useSelector((state: Store) => state.dealConfig);
  const lines = useSelector(getBackupLines);
  const isNewDeal = useSelector(getIsNewDeal);
  const dealId = useSelector(getDealId);
  const currentLine = useSelector(getCurrentLine);

  useCheckAvailability();
  useLineImpressionMetrics();

  useEffect(() => {
    const getMeta = async (): Promise<void> => {
      try {
        let metaData: DealManagementMeta;
        if (isReseller) {
          const result = (await multiPublisherRequest(environmentId, resellerMediaOwners, getMetaData, {
            cancelFunctions: {},
          })) as DealManagementMeta[];
          metaData = result.reduce<DealManagementMeta>(
            (acc, cur) => {
              acc.poi.attributes = [...acc.poi.attributes, ...cur.poi.attributes];
              acc.poi.datasources = [...acc.poi.datasources, ...cur.poi.datasources];
              acc.productCategoryGroups = [...acc.productCategoryGroups, ...cur.productCategoryGroups];
              return acc;
            },
            {
              poi: { attributes: [], datasources: [] },
              productCategoryGroups: [],
              allowedStateTransitions: {},
              originalLineStatuses: {},
            },
          );
        } else {
          metaData = await getMetaData({ cancelFunctions });
        }
        dispatch(changeMeta(metaData));
      } catch {} // eslint-disable-line no-empty
    };

    getMeta();
  }, []);

  useEffect(() => {
    if (internalId && internalId === dealId) return;

    if (internalId && dealConfig.isDealConfigFetched) {
      dispatch(
        getDealDetails(
          internalId,
          cancelFunctions,
          navigate,
          hasAdsDealLevelCPMEnabled,
          hasNonGuaranteedExtendedTargetEnabled,
          lineIdFromState,
        ),
      );
    }
  }, [internalId, cancelFunctions, dealConfig.isDealConfigFetched, dealId]);

  useEffect(() => {
    if (!internalId || internalId !== dealId) return;
    if (!hasPlannerOnlyFilters(lines, currentLine)) return;

    beginDealViewSwitch();
    navigate(`/planner/programmatic/${internalId}`, { state: { hasPlannerOnlyFilters: true } });
  }, [internalId, dealId, lines, currentLine, navigate]);

  const lineAlreadyStarted = useRef(lines.length > 0 || Boolean(currentLine.name));

  useEffect(() => {
    if (!isNewDeal || lines.length || lineAlreadyStarted.current) return;

    lineAlreadyStarted.current = true;
    dispatch(createNewLineWithUniqueName());
  }, [isNewDeal, lines, dispatch]);

  return (
    <DealWithRightSidebar
      sidebar={<RightSidebar />}
      header={<ProgrammaticCampaignHeader />}
      leftContent={<Panel />}
      rightContent={<Main />}
    />
  );
};

export default DealLines;
